import React from "react";
import { Link } from "react-router-dom";
import { Star, BadgeCheck, MapPin, Users } from "lucide-react";
import { useSite } from "../../contexts/SiteContext";
import { sportById } from "../../lib/sports";
import FavoriteButton from "../app/FavoriteButton";

export const Eyebrow = ({ children, light = false }) => (
  <div className={`otp-label inline-flex items-center gap-2 ${light ? "text-[#93C5FD]" : "text-[#1D4ED8]"}`}>
    <span className="w-6 h-[2px] bg-current" />{children}
  </div>
);

export const Rating = ({ value, count }) => {
  const { L } = useSite();
  if (!count) return <span className="text-xs font-semibold text-[#1D4ED8] bg-[#EFF4FF] rounded-full px-2 py-0.5">{L("New", "Нов")}</span>;
  return (
    <span className="inline-flex items-center gap-1 text-sm text-[#0A1628]">
      <Star className="w-4 h-4 fill-[#F59E0B] text-[#F59E0B]" /><b>{Number(value).toFixed(1)}</b><span className="text-slate-500">({count})</span>
    </span>
  );
};

export const DemoBadge = () => (
  <span className="inline-flex items-center rounded-full bg-amber-100 text-amber-800 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider" data-testid="demo-badge">Demo</span>
);

export const coverFor = (c) => c.photo_url || c.gallery_urls?.[0] || sportById(c.sports?.[0])?.img || sportById("soccer").img;

export const CoachCard = ({ c }) => {
  const { L, price, sportLabel, settings } = useSite();
  const verified = (c.badges || []).includes("verified") || (c.verification_docs || []).length > 0;
  return (
    <Link to={`/coach/${c.id}`} className="otp-card group block rounded-3xl" data-testid={`coach-card-${c.id}`}>
      <div className="relative aspect-[4/5] overflow-hidden rounded-3xl bg-slate-100">
        <img src={coverFor(c)} alt={c.name} className="w-full h-full object-cover object-top" loading="lazy" />
        <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-[#0A1628]/80 to-transparent" />
        <div className="absolute top-3 left-3 flex gap-1.5">
          {settings?.sponsored_enabled && c.is_sponsored && <span className="inline-flex items-center rounded-full bg-[#1D4ED8] text-white px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider" data-testid={`sponsored-badge-${c.id}`}>{L("Sponsored", "Спонсориран")}</span>}
          {c.is_demo ? <DemoBadge /> : verified && <span className="inline-flex items-center gap-1 rounded-full bg-white/95 px-3 py-1 text-xs font-semibold text-[#0A1628]"><BadgeCheck className="w-3.5 h-3.5 text-[#1D4ED8]" /> {L("Verified", "Проверен")}</span>}
        </div>
        {!c.is_demo && <div className="absolute top-3 right-3" onClick={(e) => e.preventDefault()}><FavoriteButton coachId={c.id} /></div>}
        <div className="absolute bottom-3 left-3 right-3 flex flex-wrap gap-1.5">
          {(c.sports || []).slice(0, 2).map((s) => <span key={s} className="rounded-full bg-white/20 backdrop-blur-md text-white text-[11px] font-semibold px-2.5 py-1">{sportLabel(s)}</span>)}
          {c.group_enabled && <span className="rounded-full bg-[#1D4ED8] text-white text-[11px] font-semibold px-2.5 py-1 inline-flex items-center gap-1"><Users className="w-3 h-3" /> {L("Group", "Група")}</span>}
        </div>
      </div>
      <div className="pt-3 px-1">
        <div className="flex items-center justify-between gap-2">
          <h3 className="font-semibold text-[15px] text-[#0A1628] truncate">{c.name}</h3>
          <Rating value={c.rating} count={c.review_count} />
        </div>
        <p className="text-sm text-slate-500 truncate inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {c.city || L("Location on request", "Локация при запитване")}{c.distance_km != null && ` · ${c.distance_km} km`}</p>
        <p className="mt-1 text-[15px] text-[#0A1628]">
          {c.hourly_rate ? <><b>{price(c.hourly_rate, c.currency)}</b> <span className="text-slate-500">/ {L("hour", "час")}</span></> : <span className="text-slate-500">{L("Price on request", "Цена при запитване")}</span>}
        </p>
      </div>
    </Link>
  );
};
